import { type ErrorInfo, StrictMode } from 'react'
import { Provider } from 'react-redux'
import { HistoryRouter as Router } from 'redux-first-history/rr6'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { ApolloProvider } from '@apollo/client'
import { QueryClientProvider } from '@tanstack/react-query'

import { ApiGatewayProvider } from '@dhl/api-gateway-provider'
import { MapProvider } from '@dhl/google-maps'
import { SnackbarProvider } from '@dhl/snackbar'
import { VersionProvider } from '@dhl/version-provider'
import { CacheProvider, ThemeProvider } from '@dhl/theme'
import { I18nProvider, useSetLanguageDocument } from '@dhl/i18n'
import { initDataLayer } from '@dhl/third-party'
import { ErrorBoundaryDeprecated } from '@dhl/error'
import { useMobileViewportHeight } from '@dhl/viewport-height'
import { BarcodeLaserListenerProvider } from '@dhl/barcode-input'
import { dataLayerPublish } from '@dhl/datalayer'
import { useSetupLocalXsrfRequestInterceptor } from '@dhl/receiver-axios'
import { type MigrationMode, useMigrationGuardWithMode } from '@dhl/domain-migration'

import { LoggedInWithBusinessAccountDialog } from '~/account/view/components/LoggedInWithBusinessAccountDialog'
import { apolloClient } from '~/apollo'
import { ConfigProvider } from '~/basket/view/withConfig'
import { history, store } from '~/redux/store'
import { Routes } from '~/Routes'

import { PromptContext } from './components/prompt'
import { queryClient } from './query-client'
import { getConfig } from './config'
import { StartUp } from '~/StartUp'
import { PwaOtcNativeBridge } from '~/PwaOtcNativeBridge'

initDataLayer()

const { apiGatewayHost, basePath, cspNonce, maps, version, migration } = getConfig()
const basePathWithoutTrailingSlash = basePath.slice(0, -1)

export const App = () => {
  const { continueRenderApp } = useMigrationGuardWithMode(migration.mode as MigrationMode)

  useSetLanguageDocument()
  useMobileViewportHeight()
  useSetupLocalXsrfRequestInterceptor(process.env.REACT_APP_ENVIRONMENT)

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    dataLayerPublish('ERROR', { id: 'error-boundary', error, errorInfo })
  }

  if (!continueRenderApp) {
    return null
  }

  return (
    <CacheProvider nonce={cspNonce}>
      <ThemeProvider>
        <ApolloProvider client={apolloClient}>
          <ErrorBoundaryDeprecated redirectUrl={basePathWithoutTrailingSlash} onError={handleError}>
            <QueryClientProvider client={queryClient}>
              <MapProvider apiKey={maps.apiKey} nonce={cspNonce}>
                <ApiGatewayProvider host={apiGatewayHost}>
                  <Provider store={store}>
                    <SnackbarProvider>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <I18nProvider>
                          <ConfigProvider>
                            <BarcodeLaserListenerProvider>
                              <Router basename={basePathWithoutTrailingSlash} history={history}>
                                <VersionProvider url="/pwa/version.json" version={version}>
                                  <PromptContext>
                                    <PwaOtcNativeBridge>
                                      <StartUp>
                                        <StrictMode>
                                          <LoggedInWithBusinessAccountDialog />
                                          <Routes />
                                        </StrictMode>
                                      </StartUp>
                                    </PwaOtcNativeBridge>
                                  </PromptContext>
                                </VersionProvider>
                              </Router>
                            </BarcodeLaserListenerProvider>
                          </ConfigProvider>
                        </I18nProvider>
                      </LocalizationProvider>
                    </SnackbarProvider>
                  </Provider>
                </ApiGatewayProvider>
              </MapProvider>
            </QueryClientProvider>
          </ErrorBoundaryDeprecated>
        </ApolloProvider>
      </ThemeProvider>
    </CacheProvider>
  )
}
